import type { FC, PropsWithChildren, ReactNode } from 'react';
import { useMemo } from 'react';

import { ActiveMenuItemProvider, useSideMenuNavigation } from '@shared/products-core';
import { ErrorBoundary } from '@shared/error-boundary';

import type { NavigationProps, StripeProps } from './LayoutProps';
import { MainLayout } from './MainLayout';
import { LayoutWithSideMenu } from './LayoutWithSideMenu';

type Props = NavigationProps &
    StripeProps & { activeMenuItem?: string; layoutProductInfo: ReactNode };

export const ProductLayout: FC<PropsWithChildren<Props>> = ({
    navigationItems,
    stripeHeader,
    children,
    activeMenuItem,
    layoutProductInfo,
    alwaysAllowStripeNavigation,
}) => {
    // @ts-expect-error legacy menu items are not strategy based, position prop is missing
    const selectedItem = useSideMenuNavigation(navigationItems, activeMenuItem);

    const accessibleNavigationItems = useMemo(
        () =>
            navigationItems.map((item) =>
                item.id === selectedItem.id ? { ...item, 'aria-current': 'page' } : item,
            ),
        [navigationItems, selectedItem],
    );

    return (
        <ErrorBoundary>
            <ActiveMenuItemProvider value={selectedItem}>
                <MainLayout
                    navigationItems={accessibleNavigationItems}
                    stripeHeader={stripeHeader}
                    alwaysAllowStripeNavigation={alwaysAllowStripeNavigation}
                >
                    <LayoutWithSideMenu
                        navigationItems={accessibleNavigationItems}
                        layoutProductInfo={layoutProductInfo}
                    >
                        {children}
                    </LayoutWithSideMenu>
                </MainLayout>
            </ActiveMenuItemProvider>
        </ErrorBoundary>
    );
};
